import { useEffect, useState, useRef } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

function ContentPostsPromProfiles({ promoProfileId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const modalRef = useRef(null);

  // Función para detectar si un URL es video
  function isVideo(url) {
    return /\.(mp4|webm|ogg)$/i.test(url);
  }

  useEffect(() => {
    async function fetchPosts() {
      if (!promoProfileId) {
        setPosts([]);
        return;
      }
      setLoading(true);

      try {
        const q = query(
          collection(db, "PromotionalPosts"),
          where("promoProfileId", "==", promoProfileId),
          where("visibilidad", "==", true),
          where("estado", "==", "activo")
        );

        const querySnapshot = await getDocs(q);

        const fetchedPosts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPosts(fetchedPosts);
      } catch (error) {
        console.error("Error al obtener posts:", error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, [promoProfileId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSelectedPost(null);
        setSelectedMediaIndex(0);
      }
    }
    if (selectedPost) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden"; // Evitar scroll background
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
      setSelectedMediaIndex(0);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [selectedPost]);

  if (loading)
    return (
      <p className="text-center text-gray-500 dark:text-gray-400 mt-8">
        Cargando publicaciones...
      </p>
    );

  if (posts.length === 0)
    return (
      <p className="text-center text-gray-600 dark:text-gray-400 mt-8">
        No hay publicaciones para este perfil.
      </p>
    );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-4 sm:px-0">
        {posts.map(post => (
          <div
            key={post.id}
            onClick={() => {
              setSelectedPost(post);
              setSelectedMediaIndex(0);
            }}
            className="cursor-pointer rounded-lg overflow-hidden bg-white dark:bg-zinc-900 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-transform duration-300 flex flex-col"
            title={post.title}
          >
            {post.images && post.images.length > 0 ? (
              <div className="relative w-full pt-[100%]">
                <img
                  src={post.images[0]}
                  alt={post.title}
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="h-0 pt-[100%] bg-gray-200 dark:bg-zinc-700" />
            )}

            <div className="p-4 flex flex-col flex-grow">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-yellow-300 truncate mb-2">{post.title}</h3>
              <p className="text-gray-600 dark:text-gray-300 flex-grow line-clamp-3">{post.description}</p>

              {post.ctaLink && (
                <a
                  href={post.ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="mt-4 inline-block self-start px-4 py-2 bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-semibold rounded-lg shadow-md transition-colors duration-300"
                >
                  {post.ctaTexto || "Ver más"}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal detalle */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-6">
          <div
            ref={modalRef}
            className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative p-6 scroll-smooth"
          >
            <button
              onClick={() => {
                setSelectedPost(null);
                setSelectedMediaIndex(0);
              }}
              aria-label="Cerrar detalle"
              className="absolute top-4 right-4 text-gray-600 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-400 text-3xl font-bold transition-colors"
            >
              &times;
            </button>

            <h2 className="text-2xl font-bold mb-4 text-yellow-800 dark:text-yellow-300">{selectedPost.title}</h2>

            {/* Media combinado (imágenes + videos) con navegación */}
            {(() => {
              const mediaItems = [...(selectedPost.images || []), ...(selectedPost.videos || [])];
              if (mediaItems.length === 0) return null;

              const currentMedia = mediaItems[selectedMediaIndex];

              return (
                <div className="relative mb-6 flex justify-center items-center">
                  {isVideo(currentMedia) ? (
                    <video
                      key={currentMedia}
                      src={currentMedia}
                      controls
                      className="max-h-96 rounded-lg mx-auto"
                    />
                  ) : (
                    <img
                      key={currentMedia}
                      src={currentMedia}
                      alt={`${selectedPost.title} ${selectedMediaIndex + 1}`}
                      className="max-h-96 rounded-lg mx-auto object-cover"
                      loading="lazy"
                    />
                  )}

                  {mediaItems.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMediaIndex(i => (i === 0 ? mediaItems.length - 1 : i - 1));
                        }}
                        aria-label="Media anterior"
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-yellow-500 text-white rounded-full p-2 hover:bg-yellow-600 transition-colors"
                      >
                        &#8249;
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMediaIndex(i => (i === mediaItems.length - 1 ? 0 : i + 1));
                        }}
                        aria-label="Media siguiente"
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-yellow-500 text-white rounded-full p-2 hover:bg-yellow-600 transition-colors"
                      >
                        &#8250;
                      </button>
                    </>
                  )}
                </div>
              );
            })()}

            <div className="space-y-3 text-gray-700 dark:text-gray-300">
              {selectedPost.description && (
                <p>
                  <strong className="font-semibold text-yellow-700 dark:text-yellow-400">Descripción:</strong> {selectedPost.description}
                </p>
              )}

              
              {selectedPost.descuento && selectedPost.descuento !== "" ? (
                <p>
                  <strong className="font-semibold text-yellow-700 dark:text-yellow-400">Descuento:</strong> {`${selectedPost.descuento}%`}
                </p>
              ) : null}


              {selectedPost.fechaInicio && selectedPost.fechaInicio !== "" && (
                <p>
                  <strong className="font-semibold text-yellow-700 dark:text-yellow-400">Fecha Inicio:</strong> {selectedPost.fechaInicio}
                </p>
              )}

              {selectedPost.fechaFin && selectedPost.fechaFin !== "" && (
                <p>
                  <strong className="font-semibold text-yellow-700 dark:text-yellow-400">Fecha Fin:</strong> {selectedPost.fechaFin}
                </p>
              )}

              {selectedPost.createdAt ? (
                <p>
                  <strong className="font-semibold text-yellow-700 dark:text-yellow-400">Creado el:</strong>{" "}
                  {new Date(selectedPost.createdAt.seconds * 1000).toLocaleString()}
                </p>
              ) : null}
            </div>

            {selectedPost.ctaLink && (
              <a
                href={selectedPost.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-6 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-bold rounded-lg shadow-lg transition-colors"
              >
                {selectedPost.ctaTexto || "Solicitar"}
              </a>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ContentPostsPromProfiles;
